import { useMemo, useState } from 'react'
import { DashboardLayout } from '../../components/layout'
import { Button, Modal } from '../../components/ui'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import { useProducts, useTeam } from '../../hooks'
import { useToast } from '../../context/ToastContext'
import TeamMemberCard from '../../features/operations/TeamMemberCard'
import TeamMemberForm from '../../features/operations/TeamMemberForm'

function TeamPage() {
  const { products, loading: productsLoading } = useProducts()
  const { members, assignments, loading: teamLoading, createTeamMember, createAssignment } = useTeam()
  const [showAddModal, setShowAddModal] = useState(false)
  const [formLoading, setFormLoading] = useState(false)
  const [assignment, setAssignment] = useState({
    team_member_id: '',
    product_id: '',
    role: '',
  })
  const toast = useToast()

  const memberAssignments = useMemo(() => {
    return members.reduce((acc, member) => {
      acc[member.id] = assignments.filter(item => item.team_member_id === member.id)
      return acc
    }, {})
  }, [members, assignments])

  const handleCreateMember = async (formData) => {
    setFormLoading(true)
    const { error } = await createTeamMember(formData)
    setFormLoading(false)

    if (error) {
      toast.error('Failed to add team member')
      return
    }

    toast.success('Team member added')
    setShowAddModal(false)
  }

  const handleAssignmentChange = (e) => {
    const { name, value } = e.target
    setAssignment(prev => ({ ...prev, [name]: value }))
  }

  const handleCreateAssignment = async (e) => {
    e.preventDefault()
    if (!assignment.team_member_id || !assignment.product_id || !assignment.role) return

    const { error } = await createAssignment(assignment)
    if (error) {
      toast.error('Failed to assign member')
      return
    }

    toast.success('Assignment created')
    setAssignment({ team_member_id: '', product_id: '', role: '' })
  }

  if (productsLoading || teamLoading) {
    return (
      <DashboardLayout title="Team">
        <LoadingSpinner.Overlay message="Loading team..." />
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout
      title="Team"
      actions={<Button onClick={() => setShowAddModal(true)}>+ Add Member</Button>}
    >
      <div className="card mb-6">
        <h3 className="text-lg font-bold mb-4">Assign Role to Product</h3>
        <form onSubmit={handleCreateAssignment} className="form-row">
          <div className="form-group">
            <label className="form-label">Team Member</label>
            <select
              className="form-select"
              name="team_member_id"
              value={assignment.team_member_id}
              onChange={handleAssignmentChange}
              required
            >
              <option value="">Select member</option>
              {members.map(member => (
                <option key={member.id} value={member.id}>{member.name}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Product</label>
            <select
              className="form-select"
              name="product_id"
              value={assignment.product_id}
              onChange={handleAssignmentChange}
              required
            >
              <option value="">Select product</option>
              {products.map(product => (
                <option key={product.id} value={product.id}>{product.name}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Role</label>
            <input
              className="form-input"
              name="role"
              value={assignment.role}
              onChange={handleAssignmentChange}
              placeholder="Product Lead"
              required
            />
          </div>

          <div className="form-group align-end">
            <Button type="submit">Assign</Button>
          </div>
        </form>
      </div>

      <div className="grid grid-3">
        {members.map(member => (
          <TeamMemberCard
            key={member.id}
            member={member}
            assignments={memberAssignments[member.id] || []}
          />
        ))}
      </div>

      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add Team Member"
      >
        <TeamMemberForm
          onSubmit={handleCreateMember}
          onCancel={() => setShowAddModal(false)}
          loading={formLoading}
        />
      </Modal>
    </DashboardLayout>
  )
}

export default TeamPage
